import {
  Accordion,
  AccordionProps,
  Alert,
  AlertProps,
  Box,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { flow, keys, omit, set, size, without } from 'lodash/fp';
import React from 'react';

import { useStoreSettings } from '@portals/api/partners';
import { ReactComponent as Danger } from '@portals/icons/linear/danger.svg';
import { ReactComponent as Trash } from '@portals/icons/linear/trash.svg';
import { VerticalScrollBar } from '@portals/scrollbar';
import { PeriodEnum } from '@portals/types';

import { AddPriceMenuButton } from './AddPriceMenuButton';
import { PaymentSettingPrices } from './PaymentSettingPrices';
import { useStoreListingPeriodPriceTitle } from '../../../hooks/store-listings.hooks';
import { PaymentSettingItemPriceType } from '../../../types/store-listings.types';
import { useStoreListingWizard } from '../hooks';

export function StandardPaymentSettings() {
  const theme = useMantineTheme();

  const { contextData, setContextData } = useStoreListingWizard();

  const storeSettings = useStoreSettings();

  const calcIsAllowedToRemovePaymentPeriod = () => {
    const { storeListing } = contextData;

    // Create mode, we can remove any period
    if (!storeListing) return true;

    // Store listing is editable, we need to validate some conditions
    if (storeListing.editable) {
      // Store listing is not published, we can remove any period
      if (storeListing.status !== 'published') {
        return true;
      }

      // Store listing is published, we can remove any period if there is more than one
      if (storeListing.status === 'published') {
        return size(contextData.paymentSettings) > 1;
      }
    }

    // Store listing is not editable, we can't remove any period
    return false;
  };

  const { getPriceTitle } = useStoreListingPeriodPriceTitle();

  const onChangeAccordion = (values: PeriodEnum[]) => {
    setContextData((prev) =>
      set(['paymentSettingsAccordionValues'], values, prev)
    );
  };

  const onAddPaymentPeriod = (period: PeriodEnum) => {
    const prices: PaymentSettingItemPriceType[] = (
      storeSettings.data?.currencies ?? []
    ).map((currency) => ({
      currency,
      amountInScu: null,
      error: false,
    }));

    setContextData((prev) =>
      flow([
        set(['paymentSettings', period, 'prices'], prices),
        set('paymentSettingsAccordionValues', [period]),
        set('paymentSettingsGeneralError', undefined),
      ])(prev)
    );
  };

  const onRemovePaymentPeriod = (period: PeriodEnum) => {
    setContextData((prev) =>
      flow([
        set('paymentSettings', omit([period], prev.paymentSettings)),
        set(
          'paymentSettingsAccordionValues',
          without([period], prev.paymentSettingsAccordionValues)
        ),
      ])(prev)
    );
  };

  if (storeSettings.isLoading || !storeSettings.data) {
    return <LoadingOverlay visible />;
  }

  return (
    <Stack spacing={32}>
      {size(contextData.paymentSettings) > 0 && (
        <VerticalScrollBar
          renderView={(props) => (
            <Box
              sx={{ zIndex: 0, borderTop: `1px solid ${theme.colors.gray[3]}` }}
              {...props}
            />
          )}
        >
          <Accordion<true>
            multiple
            styles={accordionStyles}
            value={contextData.paymentSettingsAccordionValues}
            onChange={(values: PeriodEnum[]) => onChangeAccordion(values)}
          >
            {(keys(contextData.paymentSettings) as PeriodEnum[]).map(
              (period) => {
                const hasErrors = contextData.paymentSettings[
                  period
                ]?.prices.some((price) => price.error);

                return (
                  <Accordion.Item value={period} key={period}>
                    <Accordion.Control>
                      <Group position="apart">
                        <Text data-testid={`price-row-title-${period}`}>
                          {getPriceTitle({
                            period,
                            product: contextData.storeListing?.product,
                            paymentSettings: contextData.paymentSettings,
                          })}
                        </Text>

                        <Group>
                          {hasErrors ? (
                            <Danger
                              width={20}
                              height={20}
                              color={theme.colors.red[4]}
                            />
                          ) : null}

                          {calcIsAllowedToRemovePaymentPeriod() ? (
                            <Trash
                              width={20}
                              height={20}
                              color={theme.colors.gray[4]}
                              onClick={() => onRemovePaymentPeriod(period)}
                            />
                          ) : null}
                        </Group>
                      </Group>
                    </Accordion.Control>

                    <Accordion.Panel>
                      <PaymentSettingPrices
                        period={period}
                        defaultCurrency={storeSettings.data.default_currency}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              }
            )}
          </Accordion>
        </VerticalScrollBar>
      )}

      {contextData.paymentSettingsGeneralError ? (
        <Alert radius="md" color="red" icon={<Danger />} styles={alertStyles}>
          {contextData.paymentSettingsGeneralError}
        </Alert>
      ) : null}

      <AddPriceMenuButton onAddPaymentPeriod={onAddPaymentPeriod} />
    </Stack>
  );
}

const accordionStyles: AccordionProps['styles'] = (theme) => ({
  panel: {
    width: `clamp(400px, 500px, 100%)`,
    position: 'relative',
  },
  control: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.white,
  },
});

const alertStyles: AlertProps['styles'] = (theme) => ({
  root: { backgroundColor: theme.fn.rgba(theme.colors.red[0], 0.6) },
  wrapper: { alignItems: 'center' },
  message: { color: theme.colors.red[4] },
});
